import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  wizardBlockCardClassName,
  wizardBlockShellClassName,
} from '../wizardBlockChrome';
import { Button } from '@/components/ui/button';
import { TiptapEditor } from '@/components/common/TiptapEditor';
import { HelpLabel } from '@/features/guided-help';
import { hasReglamentoPlaceholder } from '@/utils/tournament-reglamento';
import { DESCRIPTION_TEMPLATE } from '../../sports/shared/templates/basicInfoTemplates';
import { useTemplateApplyConfirm } from './useTemplateApplyConfirm';
import { TemplateReplaceDialog } from './TemplateReplaceDialog';

export interface BasicInfoDescriptionFieldProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  wrapInCard?: boolean;
  /**
   * What "Aplicar plantilla" inserts. Defaults to the wizard's, whose reglamento
   * is filled in at creation; an existing tournament passes it already resolved.
   */
  template?: string;
}

export function BasicInfoDescriptionField({
  value,
  onChange,
  required = false,
  wrapInCard = true,
  template = DESCRIPTION_TEMPLATE,
}: BasicInfoDescriptionFieldProps) {
  const {
    isConfirmOpen,
    requestApply,
    dismiss,
    confirmReplace,
  } = useTemplateApplyConfirm();

  const applyTemplate = (content: string) => onChange(content);

  // The placeholder only exists while the organizer keeps the template's
  // reglamento line untouched, so the hint disappears once they edit it.
  const hasPendingReglamento = useMemo(
    () => hasReglamentoPlaceholder(value),
    [value],
  );

  const editorBlock = (
    // Same rationale as BasicInfoPrizesField: the Label has no htmlFor, so
    // this scopes the e2e locator to this editor's unnamed role=textbox.
    <div className="space-y-2" data-guide-id="wizard.basicInfo.description">
      <HelpLabel topicId="wizard.basicInfo.description">
        Descripción del torneo {required && '*'}
      </HelpLabel>
      <TiptapEditor
        ariaLabel="Descripción"
        content={value}
        onChange={onChange}
        placeholder="Describe tu torneo, reglas especiales, información importante..."
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          requestApply(template, value, applyTemplate)
        }
      >
        Aplicar plantilla
      </Button>
      <p className="text-xs text-gray-500">
        Incluye información relevante para los participantes.
      </p>
      {hasPendingReglamento && (
        <p className="text-xs text-blue-600">
          El reglamento se completa solo al crear el torneo, según el formato
          que elijas más adelante. Si editás o borrás esa línea, se publica tal
          como la dejes.
        </p>
      )}
    </div>
  );

  const dialog = (
    <TemplateReplaceDialog
      open={isConfirmOpen}
      onOpenChange={(open) => !open && dismiss()}
      onConfirm={confirmReplace}
    />
  );

  if (!wrapInCard) {
    return (
      <>
        <div className={wizardBlockShellClassName}>{editorBlock}</div>
        {dialog}
      </>
    );
  }

  return (
    <Card className={wizardBlockCardClassName}>
      <CardHeader>
        <CardTitle className="text-lg">Descripción</CardTitle>
      </CardHeader>
      <CardContent>
        {editorBlock}
        {dialog}
      </CardContent>
    </Card>
  );
}
